import { Box, Button, Flex, Icon, Spinner, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import { FiCheck, FiCircle, FiZap } from "react-icons/fi";
import {
  type SourcingEventDto,
  type SourcingEventFieldSourceDto,
  useGetSourcingEventQuery,
  useListSourcingEventFieldSourcesQuery,
} from "../../autogen/bff-api";
import { HorizontalScrollBox } from "../HorizontalScrollBox";
import { alignItemToRowStart } from "../scroll";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";
import type { OrgWizardAccess } from "../sourcing-events/buyer/wizardSteps";
import { useAppDispatch } from "../redux/hooks";
import { useSub } from "../subscription/useSub";
import { creationProgress, type AreaStatus, type CreationArea } from "./creationAreas";
import { fetchSettledCreationProgress } from "./fetchSettledCreationProgress";
import { withholdsPeriodFields } from "./wizardHiddenFields";
import { rememberCreationProgress, rememberCreationProgressRefresh, type ShownCreationArea } from "./pageEntityNames";

const STATUS_ICON: Record<AreaStatus, IconType> = { done: FiCheck, filledByAi: FiZap, remaining: FiCircle };

export const CreationProgress = ({
  procurementId,
  onRevisit,
  isDisabled,
  regulationAwaitsConfirmation = false,
}: {
  procurementId: string | null;
  onRevisit: (area: CreationArea, request: string) => void;
  isDisabled: boolean;
  regulationAwaitsConfirmation?: boolean;
}) => {
  const { t } = useTranslation();
  const sub = useSub();
  const access: OrgWizardAccess = {
    hasPublicProcurementAccess: useRunsPublicProcurements(),
    hasProductAccess: sub.hasProductAccess,
  };
  const event = useGetSourcingEventQuery(procurementId ? { eventId: procurementId, recordView: false } : skipToken);
  const sources = useListSourcingEventFieldSourcesQuery(procurementId ? { eventId: procurementId } : skipToken);
  const areaLabels: Record<CreationArea, string> = {
    basis: t("Basis"),
    regulation: t("Regulations"),
    qualification: t("Qualifications"),
    criteria: t("Criteria"),
    requirements: t("Requirements"),
    deadlines: t("Deadlines"),
    invitations: t("Invitations"),
  };
  const statusLabels: Record<AreaStatus, string> = {
    done: t("Done"),
    filledByAi: t("Filled in by the AI"),
    remaining: t("Remaining"),
  };
  const shownFor = (data: SourcingEventDto | undefined, fields: SourcingEventFieldSourceDto[]): ShownCreationArea[] =>
    creationProgress(data, fields, access, { regulationAwaitsConfirmation }).map(({ area, status }) => ({
      label: areaLabels[area],
      status: statusLabels[status],
      remaining: status === "remaining",
    }));
  const latestShownFor = useRef(shownFor);
  useEffect(() => {
    latestShownFor.current = shownFor;
  });
  const loaded = !event.isError && !sources.isError && (!procurementId || (!!event.data && !!sources.data));
  const shownKey = loaded ? JSON.stringify(shownFor(event.data, sources.data?.fields ?? [])) : null;
  const holdsBack = withholdsPeriodFields(event.data);
  useEffect(() => {
    rememberCreationProgress(shownKey ? (JSON.parse(shownKey) as ShownCreationArea[]) : undefined, holdsBack);
  }, [shownKey, holdsBack]);
  useEffect(() => () => rememberCreationProgress(undefined), []);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!procurementId) return;
    rememberCreationProgressRefresh(async () => {
      const fresh = await dispatch(fetchSettledCreationProgress({ procurementId })).unwrap();
      rememberCreationProgress(latestShownFor.current(fresh.event, fresh.fields), withholdsPeriodFields(fresh.event));
    });
    return () => rememberCreationProgressRefresh(undefined);
  }, [procurementId, dispatch]);
  if (procurementId && (event.isError || sources.isError)) {
    return (
      <Flex align="center" gap="3" mb="3" flexWrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {t("The progress could not be loaded.")}
        </Text>
        <Button
          variant="link"
          colorScheme="teal"
          size="sm"
          fontWeight="normal"
          onClick={() => {
            void event.refetch();
            void sources.refetch();
          }}
        >
          {t("Try again")}
        </Button>
      </Flex>
    );
  }
  if (procurementId && (!event.data || !sources.data)) {
    return (
      <Flex mb="3" aria-label={String(t("Loading progress"))}>
        <Spinner size="sm" color="smPrimary" />
      </Flex>
    );
  }
  const areas = creationProgress(event.data, sources.data?.fields ?? [], access, { regulationAwaitsConfirmation });
  const revisitRequest = (area: CreationArea, status: AreaStatus) =>
    status === "remaining"
      ? t("I want to go through «{{area}}».", { area: areaLabels[area] })
      : t("I want to go through «{{area}}» again.", { area: areaLabels[area] });

  return (
    <ProgressRow
      areas={areas}
      label={String(t("Progress"))}
      button={(area, status) => (
        <Button
          size="sm"
          variant="ghost"
          fontWeight="medium"
          color={status === "remaining" ? "smTextUpcoming" : "smText"}
          leftIcon={<StatusCircle status={status} />}
          onClick={() => onRevisit(area, revisitRequest(area, status))}
          isDisabled={isDisabled}
          title={statusLabels[status]}
        >
          {areaLabels[area]}
          <Text as="span" srOnly>
            {`: ${statusLabels[status]}`}
          </Text>
        </Button>
      )}
    />
  );
};

const ProgressRow = ({
  areas,
  label,
  button,
}: {
  areas: { area: CreationArea; status: AreaStatus }[];
  label: string;
  button: (area: CreationArea, status: AreaStatus) => ReactNode;
}) => {
  const firstRemaining = areas.findIndex(({ status }) => status === "remaining");
  const lastFinished = firstRemaining > 0 ? areas[firstRemaining - 1].area : null;
  const scrollerRef = useRef<HTMLElement | null>(null);
  const lastFinishedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollerRef.current && lastFinishedRef.current) alignItemToRowStart(scrollerRef.current, lastFinishedRef.current);
  }, [lastFinished]);

  return (
    <HorizontalScrollBox mb="2" scrollerRef={scrollerRef}>
      <Flex as="nav" aria-label={label} gap="1" p="1" flexWrap={{ base: "nowrap", md: "wrap" }}>
        {areas.map(({ area, status }) => (
          <Box key={area} flexShrink={0} ref={area === lastFinished ? lastFinishedRef : undefined}>
            {button(area, status)}
          </Box>
        ))}
      </Flex>
    </HorizontalScrollBox>
  );
};

const StatusCircle = ({ status }: { status: AreaStatus }) => (
  <Flex
    align="center"
    justify="center"
    w="20px"
    h="20px"
    borderRadius="full"
    border="2px solid"
    borderColor={status === "remaining" ? "smBorder" : "smPrimary"}
    bg={status === "done" ? "smPrimary" : "transparent"}
    color={status === "done" ? "white" : "smPrimary"}
    aria-hidden
  >
    {status !== "remaining" && <Icon as={STATUS_ICON[status]} w="10px" h="10px" />}
  </Flex>
);
